import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { HTMLProps, useState } from 'react';
import { tv } from 'tailwind-variants';

import { CheckboxCheckedIcon } from '@/ui/Icons/CheckboxCheckedIcon';
import { IndeterminateIcon } from '@/ui/Icons/IndeterminateIcon';

const checkboxStyles = tv({
  slots: {
    root: [
      'h-[18px]',
      'w-[18px]',
      'flex',
      'items-center',
      'justify-center',
      'rounded-[6px]',
      'cursor-pointer',
      'bg-transparent',
      'border-solid',
      'border-2',
      'border-token-gray-700',
      'p-1',
      'text-special-content-button-purple',
      'hover:border-token-gray-800',
      'focus-visible:outline-none',
      'focus-visible:ring-4',
      'focus-visible:ring-token-purple-400',
      'disabled:text-token-gray-400',
      'disabled:border-token-gray-500',
      'disabled:cursor-not-allowed',
      'data-[state=checked]:bg-token-purple-1000',
      'data-[state=checked]:border-none',
      'data-[state=checked]:shadow-15',
      'data-[state=checked]:opacity-100',
      'data-[state=checked]:disabled:bg-token-gray-400',
      'data-[state=indeterminate]:bg-token-purple-1000',
      'data-[state=indeterminate]:border-none',
      'data-[state=indeterminate]:shadow-15',
      'data-[state=indeterminate]:opacity-100',
      'data-[state=indeterminate]:disabled:bg-token-gray-400',
      'data-[state=checked]:focus-visible:ring-token-purple-200',
      'data-[state=indeterminate]:focus-visible:ring-token-purple-200',
    ],
    indicator: ['flex', 'items-center', 'justify-center'],
  },
});

export type CheckedStates = 'checked' | 'unchecked' | 'indeterminate';

type CheckboxProps = {
  /** The ID of the checkbox. Should be associated with a label using `htmlFor`. */
  id?: string;

  /** The checked state of the checkbox.
   * If `undefined` the component will be uncontrolled.
   * In this case, the component will never enter an indeterminate state.
   */
  checked?: CheckedStates;

  /** The initial checked state of the checkbox.*/
  defaultChecked?: CheckedStates;

  /** Callback that fires when the checked state changes. */
  onCheckedChange?: (checked: CheckedStates) => void;

  /** The disabled state of the checkbox. */
  disabled?: boolean;
};

export function Checkbox({
  id,
  checked,
  defaultChecked,
  onCheckedChange,
  disabled,
  ...props
}: CheckboxProps &
  Omit<HTMLProps<HTMLButtonElement>, 'ref' | 'onChange' | 'type' | keyof CheckboxProps>) {
  const [checkedState, setCheckedState] = useState<CheckedStates>(
    checked || defaultChecked || 'unchecked',
  );

  const isControlled = checked !== undefined;

  const onCheckedChangeHandler = (newVal: CheckedStates) => {
    // only change if the component is uncontrolled
    if (isControlled) {
      setCheckedState(newVal);
    }
    if (onCheckedChange) {
      onCheckedChange(newVal);
    }
  };

  const value = isControlled ? checked : checkedState;
  const { root, indicator } = checkboxStyles();

  return (
    <RadixCheckbox.Root
      id={id}
      className={root()}
      checked={value === 'indeterminate' ? value : value === 'checked'}
      disabled={disabled}
      onCheckedChange={(newState) => onCheckedChangeHandler(newState ? 'checked' : 'unchecked')}
      {...props}
    >
      <RadixCheckbox.Indicator className={indicator()}>
        {value === 'checked' && <CheckboxCheckedIcon width={10} />}
        {value === 'indeterminate' && <IndeterminateIcon width={10} />}
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );
}
